// src/components/widgets/QuestionFormDialog.jsx
import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';

const QuestionFormDialog = ({ isOpen, onClose, onSave, initialData = null }) => {
  const [formData, setFormData] = useState({
    id: '',
    label: '',
    type: 'text',
    options: [],
    required: false,
    placeholder: '',
    help_text: '',
    validation: {
      min_length: '',
      max_length: ''
    },
    display_order: 0,
    show_on_step: 'contact'
  });

  const [optionInput, setOptionInput] = useState('');

  // Initialize form when dialog opens or initial data changes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          ...initialData,
          validation: initialData.validation || { min_length: '', max_length: '' }
        });
      } else {
        // Reset to defaults for new question
        setFormData({
          id: `question-${Date.now()}`,
          label: '',
          type: 'text',
          options: [],
          required: false,
          placeholder: '',
          help_text: '',
          validation: {
            min_length: '',
            max_length: ''
          },
          display_order: 0,
          show_on_step: 'contact'
        });
      }
      setOptionInput('');
    }
  }, [isOpen, initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleValidationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      validation: {
        ...prev.validation,
        [field]: value
      }
    }));
  };

  const handleAddOption = () => {
    if (optionInput.trim()) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, optionInput.trim()]
      }));
      setOptionInput('');
    }
  };

  const handleRemoveOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = () => {
    // Validation
    if (!formData.label.trim()) {
      alert('Question label is required');
      return;
    }

    if (['select', 'radio'].includes(formData.type) && formData.options.length === 0) {
      alert('At least one option is required for select and radio questions');
      return;
    }

    // Clean up validation fields if empty
    const cleanedFormData = {
      ...formData,
      validation: {
        min_length: formData.validation.min_length ? parseInt(formData.validation.min_length) : undefined,
        max_length: formData.validation.max_length ? parseInt(formData.validation.max_length) : undefined
      }
    };

    onSave(cleanedFormData);
    onClose();
  };

  const questionTypes = [
    { value: 'text', label: 'Short Text' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'select', label: 'Dropdown' },
    { value: 'radio', label: 'Radio Buttons' },
    { value: 'checkbox', label: 'Checkbox' }
  ];

  const showOptions = ['select', 'radio'].includes(formData.type);
  const showValidation = ['text', 'textarea'].includes(formData.type);
  const showPlaceholder = ['text', 'textarea'].includes(formData.type);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Edit Question' : 'Add Custom Question'}
          </DialogTitle>
          <DialogDescription>
            Create a custom question to collect additional information from customers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Question Label */}
          <div className="space-y-2">
            <Label htmlFor="question-label">Question Label *</Label>
            <Input
              id="question-label"
              value={formData.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="What service do you need?"
            />
          </div>

          {/* Question Type */}
          <div className="space-y-2">
            <Label htmlFor="question-type">Question Type *</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => handleChange('type', value)}
            >
              <SelectTrigger id="question-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {questionTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Options (for select/radio) */}
          {showOptions && (
            <div className="space-y-2">
              <Label>Options *</Label>
              <div className="flex gap-2">
                <Input
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                  placeholder="Add an option..."
                />
                <Button
                  type="button"
                  onClick={handleAddOption}
                  size="sm"
                  variant="outline"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.options.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.options.map((option, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {option}
                      <button
                        onClick={() => handleRemoveOption(index)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Placeholder */}
          {showPlaceholder && (
            <div className="space-y-2">
              <Label htmlFor="question-placeholder">Placeholder</Label>
              <Input
                id="question-placeholder"
                value={formData.placeholder}
                onChange={(e) => handleChange('placeholder', e.target.value)}
                placeholder="Optional placeholder text..."
              />
            </div>
          )}

          {/* Help Text */}
          <div className="space-y-2">
            <Label htmlFor="question-help">Help Text</Label>
            <Input
              id="question-help"
              value={formData.help_text}
              onChange={(e) => handleChange('help_text', e.target.value)}
              placeholder="Optional help text to display below the question"
            />
          </div>

          {/* Validation (for text/textarea) */}
          {showValidation && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min-length">Min Length</Label>
                <Input
                  id="min-length"
                  type="number"
                  min="0"
                  value={formData.validation.min_length}
                  onChange={(e) => handleValidationChange('min_length', e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max-length">Max Length</Label>
                <Input
                  id="max-length"
                  type="number"
                  min="0"
                  value={formData.validation.max_length}
                  onChange={(e) => handleValidationChange('max_length', e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
          )}

          {/* Show on Step */}
          <div className="space-y-2">
            <Label htmlFor="show-on-step">Show on Step</Label>
            <Select
              value={formData.show_on_step}
              onValueChange={(value) => handleChange('show_on_step', value)}
            >
              <SelectTrigger id="show-on-step">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="service">Service Selection Step</SelectItem>
                <SelectItem value="contact">Contact Information Step</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Required */}
          <div className="flex items-center space-x-2">
            <Switch
              id="question-required"
              checked={formData.required}
              onCheckedChange={(checked) => handleChange('required', checked)}
            />
            <Label htmlFor="question-required" className="cursor-pointer">
              Required field
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            {initialData ? 'Save Changes' : 'Add Question'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionFormDialog;
